/**
 * Ingredients-list and preparation-steps text/HTML rendering — pure string
 * building over RecipeStore data, ported from GraphVisualizer where it
 * lived only because GraphVisualizer used to be the app's sole view. Used
 * by GraphVisualizer's own `buildIngredientsPanelHtml()`/
 * `buildPreparationPanelHtml()` (in turn called by the standalone
 * IngredientsView/PreparationView) and by NodeVisualizer's step/ingredients
 * text renderers (via `setStepTextRenderer()`/`setIngredientsTextRenderer()`).
 *
 * Every function here takes a `RecipeTextContext` instead of relying on
 * `this` — `store` for node/pool lookups, `scaleAmount` for the
 * yield-scaling factor, which is deliberately per-view ephemeral display
 * state (not store state — see GraphVisualizer's own `_scalingFactor` doc
 * comment), so it's threaded in as a plain function rather than read from
 * the store.
 */
import type { RecipeStore } from '../store/types.js';
import type { DisplayNode } from './types.js';
import type { Quantity, Tool, TransformationInfo } from '../api/types.js';
import { evaluateTemplate, TemplateContext, TemplateNode } from '../template-engine.js';
import { unitCache } from '../unit-cache.js';
import { escapeHtml } from '../ui/escapeHtml.js';
import { computeSchedule } from './schedule.js';
import {
  capitalizeFirst, capitalizeFirstVisible, decapitalizeFirstVisible, durationBadgeHtml, formatNumber,
  ingredientThumbnailHtml, mergeQuantities, preposition,
} from './textFormatting.js';

export interface RecipeTextContext {
  store: RecipeStore;
  /** Apply the current (ephemeral, per-view) yield-scaling factor to an amount. */
  scaleAmount: (amount: number) => number;
}

/**
 * Whether a referenced node should be left out of elaboration text: either
 * the ephemeral fold-state hiding (`isNodeHidden`, only when `filterHidden`
 * is requested — the panels themselves pass `filterHidden=false` to ignore
 * it) or the persisted, always-on `hidden` flag the user set explicitly on
 * that node (never appears in the elaboration, regardless of fold state).
 */
function isExcludedFromText(ctx: RecipeTextContext, node: DisplayNode, filterHidden: boolean): boolean {
  return (filterHidden && ctx.store.isNodeHidden(node.id)) || !!node.data.hidden;
}

/**
 * Build HTML for the ingredients list from a set of nodes.
 * Collects visible leaf nodes with ingredients, groups by ingredient ID,
 * and merges quantities per unit.
 */
export function buildIngredientsHtml(ctx: RecipeTextContext, nodes: DisplayNode[], _rootNode: DisplayNode): string {
  // The nodes list is already computed as ingredient nodes (fold-aware).
  // Filter to nodes that actually have ingredient info.
  const ingredientNodes = nodes.filter(n => n.data.ingredientInfo?.ingredient);

  // Group by ingredient ID
  const groups = new Map<string, DisplayNode[]>();
  for (const node of ingredientNodes) {
    const key = node.data.ingredientInfo!.ingredient!.id;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(node);
  }

  const seenKeys = new Set<string>();
  let html = '';

  for (const node of ingredientNodes) {
    const key = node.data.ingredientInfo!.ingredient!.id;
    if (seenKeys.has(key)) continue;
    seenKeys.add(key);

    const groupNodes = groups.get(key)!;
    const ingredient = groupNodes[0].data.ingredientInfo!.ingredient!;

    // Merge quantities by unit
    const quantities = groupNodes
      .map(n => n.data.ingredientInfo?.quantity)
      .filter((q): q is Quantity => q != null);
    const merged = mergeQuantities(quantities);

    // Build display string
    const qtyStr = merged.length > 0
      ? merged.map(q => {
          const scaledAmount = ctx.scaleAmount(q.amount);
          const unitStr = unitCache.getDisplayName(q.unit, scaledAmount);
          return `${formatNumber(scaledAmount)} ${unitStr}`;
        }).join(' + ')
      : '';

    const primaryNodeId = groupNodes[0].id;

    html += '<div class="ingredient-row border-b border-[var(--accent-alpha-13)] cursor-pointer py-1" data-node-id="' + escapeHtml(primaryNodeId) + '">';
    html += '  <div class="flex items-start gap-2">';
    html += ingredientThumbnailHtml(ingredient.images);
    html += '    <span>';  // wrapper to keep qty+ingredient as a single flex child
    if (qtyStr) {
      const prep = preposition(ingredient.name);
      html += `<span class="text-[#e0e0e0] font-mono text-sm">${qtyStr} ${prep}</span>`;
      html += `<span class="text-[#e0e0e0] font-mono text-sm">${escapeHtml(ingredient.name)}</span>`;
    } else {
      html += `  <span class="text-[#e0e0e0] font-mono text-sm">${escapeHtml(ingredient.name)}</span>`;
    }
    html += '    </span>';
    html += '  </div>';

    // Show comment lines below the ingredient row
    const lineNodes = groupNodes[0];
    const commentLines: string[] = [];
    if (lineNodes.data.description) commentLines.push(lineNodes.data.description);
    if (lineNodes.data.ingredientInfo?.description) commentLines.push(lineNodes.data.ingredientInfo.description);
    for (const ti of lineNodes.data.transformationInfos ?? []) {
      if (ti.description) commentLines.push(ti.description);
    }
    for (const line of commentLines) {
      html += `  <div class="text-xs text-[#888] font-mono ml-4 mt-1">// ${escapeHtml(line)}</div>`;
    }
    html += '</div>';
  }

  if (!html) {
    html = '<div class="text-sm text-[#888] font-mono">(no hi ha ingredients)</div>';
  }

  return html;
}

/**
 * Whether a tool usage is a *new physical instance* — explicitly marked
 * fresh, or with no child carrying the same tool (nothing it could be
 * continuing from). Same rule as NodeVisualizer's own
 * isToolUsageImplicitlyFresh(), kept independent since this module has no
 * reason to depend on the editor.
 */
function isNewToolInstance(ctx: RecipeTextContext, node: DisplayNode, toolId: string, fresh: boolean | undefined): boolean {
  if (fresh) return true;
  const children = (node.data.children || [])
    .map(cid => ctx.store.getNode(cid))
    .filter((cn): cn is DisplayNode => cn !== undefined);
  return !children.some(c => c.data.toolInfos?.some(ti => ti.tool.id === toolId));
}

/**
 * Build HTML for the utensils list from a set of nodes (ingredient nodes
 * + preparation-step nodes combined — a tool usage can land on either
 * kind, see isBareToolNode()'s doc comment). Each *new physical instance*
 * (see isNewToolInstance()) counts once, so a pot continuing through five
 * steps appears once while two independently introduced pans appear as
 * "2 × Paella". A usage marked `hidden` is left out unless `includeHidden`
 * is set — the Utensilis panel's own "show hidden" toggle, independent of
 * the preparation text, which never counts a hidden usage either way.
 * Within a group, usages with `counts === false` (e.g. a 2nd/3rd knife
 * nobody cares to count) don't add to the displayed number — but the
 * group still shows at least once even if every usage in it is
 * non-counting, so the tool itself is never hidden by this.
 */
export function buildToolsHtml(ctx: RecipeTextContext, nodes: DisplayNode[], includeHidden = false): string {
  const newInstances: { tool: Tool; nodeId: string; counts: boolean }[] = [];
  for (const node of nodes) {
    for (const toolInfo of node.data.toolInfos ?? []) {
      if (toolInfo.hidden && !includeHidden) continue;
      if (isNewToolInstance(ctx, node, toolInfo.tool.id, toolInfo.fresh)) {
        newInstances.push({ tool: toolInfo.tool, nodeId: node.id, counts: toolInfo.counts !== false });
      }
    }
  }

  const groups = new Map<string, { tool: Tool; nodeId: string; counts: boolean }[]>();
  for (const usage of newInstances) {
    if (!groups.has(usage.tool.id)) groups.set(usage.tool.id, []);
    groups.get(usage.tool.id)!.push(usage);
  }

  const seenKeys = new Set<string>();
  let html = '';

  for (const usage of newInstances) {
    const key = usage.tool.id;
    if (seenKeys.has(key)) continue;
    seenKeys.add(key);

    const groupUsages = groups.get(key)!;
    const tool = groupUsages[0].tool;
    const count = groupUsages.filter(u => u.counts).length || 1;
    const primaryNodeId = groupUsages[0].nodeId;

    html += '<div class="tool-row border-b border-[var(--accent-alpha-13)] cursor-pointer py-1" data-node-id="' + escapeHtml(primaryNodeId) + '">';
    html += '  <div class="flex items-start gap-2">';
    html += ingredientThumbnailHtml(tool.images);
    html += '    <span>';
    if (count > 1) {
      html += `<span class="text-[#e0e0e0] font-mono text-sm">${count} × </span>`;
    }
    html += `<span class="text-[#e0e0e0] font-mono text-sm">${escapeHtml(tool.name)}</span>`;
    html += '    </span>';
    html += '  </div>';
    html += '</div>';
  }

  if (!html) {
    html = '<div class="text-sm text-[#888] font-mono">(no hi ha utensilis)</div>';
  }

  return html;
}

/**
 * One row of the numbered preparation list — one transformation usage (or,
 * for a node with no renderable usage, a single placeholder row) — as
 * structured data rather than pre-built HTML. The single source of truth
 * for `buildPreparationHtml()` below and for the Timeline view's boxes, so
 * the two can never again disagree on which step owns which duration (see
 * this function's own doc comment for the bug this replaced).
 */
export interface PreparationStep {
  node: DisplayNode;
  stepNumber: number;
  /** null for the placeholder row (no renderable transformation usage). */
  trafoInfo: TransformationInfo | null;
  /** Seconds; trafoInfo?.duration ?? null. */
  duration: number | null;
  /** Seconds, always <= duration; trafoInfo?.attentionDuration ?? null —
   *  see schedule.ts's computeSchedule() for how the two combine. */
  attentionDuration: number | null;
  /** Short label for a narrow context (Timeline box) — the transformation's
   *  own name, capitalized, or the node's resolved title as a fallback. */
  label: string;
  /** The same rendered HTML buildPreparationHtml() shows for this row, or
   *  null for the placeholder row's "(pas sense descripció)" text. */
  text: string | null;
}

/**
 * Build the ordered, numbered step list for a set of nodes (fold-aware
 * preparation-step nodes, topologically sorted bottom-to-top). Each
 * transformation usage on a node is its own step, with its own duration —
 * paired directly with whichever usage actually produced its text, not by
 * array index against the node's full `transformationInfos` list. The old
 * `buildPreparationHtml()` did exactly that index-based pairing against a
 * *pre-filtered* text array (usages with no `transformation` dropped out of
 * the text array but not out of the duration/description lookup), which
 * could silently attribute one usage's duration/notes to a different one
 * whenever a usage in between was dropped. A node with no renderable usage
 * at all (no children, or no usage has a `transformation`) still gets
 * exactly one placeholder step, falling back to its first usage (if any)
 * for duration/description — matching the single-row fallback the old
 * lookup always landed on in that case, which was harmless there since
 * there was never more than one row to misattribute.
 */
export function buildPreparationSteps(ctx: RecipeTextContext, nodes: DisplayNode[]): PreparationStep[] {
  if (nodes.length === 0) return [];
  // stepOrder is maintained (by stepOrder.ts's cascade-repair algorithm,
  // via RecipeStore.moveNodeToStepOrder()) to already be a valid
  // topological order — a node's stepOrder is always less than every one
  // of its parents' and greater than every one of its children's — so
  // this is a plain sort, not a traversal/search the way the old
  // priority-driven topologicalSortBottomToTop() used to be.
  const sorted = [...nodes].sort((a, b) => a.data.stepOrder - b.data.stepOrder);
  const steps: PreparationStep[] = [];
  let stepNumber = 0;

  sorted.forEach((node) => {
    const children = (node.data.children || [])
      .map(cid => ctx.store.getNode(cid))
      .filter((cn): cn is DisplayNode => cn !== undefined && !isExcludedFromText(ctx, cn, false));
    const transformationInfos = node.data.transformationInfos ?? [];

    const rendered = children.length === 0
      ? []
      : transformationInfos
          .map(trafoInfo => ({ trafoInfo, text: renderStepTextForUsage(ctx, node, children, trafoInfo, false) }))
          .filter((row): row is { trafoInfo: TransformationInfo; text: string } => row.text !== null);

    if (rendered.length === 0) {
      stepNumber += 1;
      const fallback = transformationInfos[0] ?? null;
      steps.push({
        node, stepNumber, trafoInfo: fallback,
        duration: fallback?.duration ?? null,
        attentionDuration: fallback?.attentionDuration ?? null,
        label: node.name || '…',
        text: null,
      });
      return;
    }

    for (const { trafoInfo, text } of rendered) {
      stepNumber += 1;
      steps.push({
        node, stepNumber, trafoInfo,
        duration: trafoInfo.duration ?? null,
        attentionDuration: trafoInfo.attentionDuration ?? null,
        label: trafoInfo.transformation?.name ? capitalizeFirst(trafoInfo.transformation.name) : (node.name || '…'),
        text,
      });
    }
  });

  return steps;
}

/**
 * Build HTML for the preparation steps from a set of nodes — just
 * buildPreparationSteps()'s stepOrder-sorted list (see there), rendered
 * with each step's transformation info and children.
 */
export function buildPreparationHtml(ctx: RecipeTextContext, nodes: DisplayNode[], _rootNode: DisplayNode): string {
  const steps = buildPreparationSteps(ctx, nodes);
  if (steps.length === 0) {
    return '<div class="text-sm text-[#888] font-mono">(sense passos de preparació)</div>';
  }

  // Edit mode only: a dedicated drag handle, the sole drag-start target
  // (PreparationDragController.registerRow() wires its listeners onto
  // it specifically, not the row) — spans the row's full height via
  // align-self:stretch, so it's still a decent touch target even on a
  // multi-line step. Without it the whole row was draggable, which on
  // touch meant an ordinary scroll swipe anywhere on a step could be
  // mistaken for (or had to fight against) starting a drag.
  const editMode = ctx.store.isEditMode();
  const dragHandleHtml = editMode
    ? '<span class="preparation-step-drag-handle" style="cursor:grab;color:var(--accent-alpha-40);font-size:13px;line-height:1;user-select:none;touch-action:none;flex-shrink:0;align-self:stretch;display:flex;align-items:center;padding:0 8px 0 2px;">⣿</span>'
    : '';

  let html = '';
  let lastNodeId: string | null = null;

  steps.forEach((step) => {
    const { node, stepNumber, trafoInfo, duration, text } = step;
    const isFirstRowForNode = node.id !== lastNodeId;
    lastNodeId = node.id;

    html += `<div class="preparation-step-row border-b border-[var(--accent-alpha-13)] py-2 flex items-start" data-node-id="${escapeHtml(node.id)}">`;
    html += dragHandleHtml;
    html += `  <div class="flex-1 min-w-0 cursor-pointer">`;
    html += `  <div class="flex items-baseline gap-2">`;
    html += `    <span class="text-xs text-[var(--accent)] font-bold font-mono flex-shrink-0">${stepNumber}.</span>`;
    if (text) {
      html += `    <span class="flex-1 text-sm text-[#e0e0e0] font-mono leading-relaxed">${text}</span>`;
    } else {
      html += `    <span class="flex-1 text-sm text-[#888] font-mono">(pas sense descripció)</span>`;
    }
    // Duration ("durada") — a right-aligned clock badge next to the
    // step's own text, rather than appended into the text itself (see
    // buildStepTextHardcoded()'s doc comment).
    html += durationBadgeHtml(duration);
    html += `  </div>`;

    // Show comment lines below the step — the node's own description,
    // ingredient notes and tool notes only appear once, on the first row;
    // each transformation's own description appears on its own row.
    const commentLines: string[] = [];
    if (isFirstRowForNode && node.data.description) commentLines.push(node.data.description);
    if (isFirstRowForNode && node.data.ingredientInfo?.description) commentLines.push(node.data.ingredientInfo.description);
    if (trafoInfo?.description) commentLines.push(trafoInfo.description);
    if (isFirstRowForNode) {
      for (const toolInfo of node.data.toolInfos ?? []) {
        if (!toolInfo.hidden && toolInfo.description) commentLines.push(toolInfo.description);
      }
    }
    for (const line of commentLines) {
      html += `  <div class="text-xs text-[#888] font-mono ml-6 mt-1">// ${escapeHtml(line)}</div>`;
    }
    html += '  </div>'; // .flex-1 content wrapper
    html += '</div>';
  });

  return html;
}

/**
 * Total elaboration time for a node and everything under it — sums (not
 * max; sequential by default until a future parallel-steps feature
 * exists) this node's own transformation usages' durations with every
 * child's own total, recursively. Counts *all* children regardless of
 * the node-level `hidden` flag — hidden only affects step-text
 * narration, never real elapsed time. Returns null (not 0) when nothing
 * in the subtree has a duration set, so callers can skip rendering
 * instead of showing "0 segons" everywhere.
 *
 * Deliberately never stored — computed fresh from whatever children
 * currently exist, the same way DisplayNode.leafDistance is (see
 * RecipeStore._computeNodeLevels()). This is what makes "swapping in an
 * elaboration subgraph transfers its time to the parent" come for free:
 * the moment new children exist under a node, this picks them up on the
 * very next call, with no special-cased transfer logic anywhere.
 */
export function computeNodeTotalDuration(ctx: RecipeTextContext, node: DisplayNode): number | null {
  const subtree = [node, ...ctx.store.getDescendantsInPool(node)]
    .sort((a, b) => a.data.stepOrder - b.data.stepOrder);

  // Every transformation usage in the subtree becomes its own schedulable
  // unit, regardless of whether buildPreparationSteps() would render it
  // as a real step (a node with no children of its own never gets more
  // than one rendered/placeholder row there, which would silently drop
  // a 2nd+ usage's duration here — this stays exhaustive, same as the
  // summing this replaces, just duration-aware about overlap via
  // computeSchedule() instead of blindly adding everything up). The
  // other fields below are never read by computeSchedule() itself.
  const steps: PreparationStep[] = [];
  let hasAnyDuration = false;
  for (const n of subtree) {
    for (const trafoInfo of n.data.transformationInfos ?? []) {
      if (trafoInfo.duration) hasAnyDuration = true;
      steps.push({
        node: n, stepNumber: 0, trafoInfo,
        duration: trafoInfo.duration ?? null,
        attentionDuration: trafoInfo.attentionDuration ?? null,
        label: '', text: null,
      });
    }
  }
  if (!hasAnyDuration) return null;

  const schedule = computeSchedule(steps);
  return Math.max(0, ...schedule.map((s) => s.finishSeconds));
}

// ============================================================================
// STEP TEXT RENDERING (for read-only node card)
// ============================================================================

/**
 * Render step text for every transformation usage on a node, in order —
 * each one gets its own complete clause (label + the node's children),
 * rather than being merged into one combined sentence. A node with no
 * transformations, or no children, renders no steps at all (empty array).
 * This is the "always together, in order, as separate steps" rule a node's
 * ordered transformation list uses for now (see RecipeNodeBase.recipe_transformations'
 * doc comment) — simpler than trying to chain them into one sentence, and
 * can be revisited later.
 */
export function renderStepTextsForNode(ctx: RecipeTextContext, node: DisplayNode, filterHidden = true): string[] {
  const children = (node.data.children || [])
    .map(cid => ctx.store.getNode(cid))
    .filter((cn): cn is DisplayNode => cn !== undefined && !isExcludedFromText(ctx, cn, filterHidden));

  if (children.length === 0) return [];

  return (node.data.transformationInfos ?? [])
    .map(trafoInfo => renderStepTextForUsage(ctx, node, children, trafoInfo, filterHidden))
    .filter((text): text is string => text !== null);
}

/**
 * Convenience single-string wrapper for callers that just want a quick
 * preview (the read-only node card, List View's expanded row detail) —
 * every usage's clause, joined with a line break. Returns null when there's
 * nothing to show, same as before this became a list.
 */
export function renderStepTextForNode(ctx: RecipeTextContext, node: DisplayNode, filterHidden = true): string | null {
  const texts = renderStepTextsForNode(ctx, node, filterHidden);
  return texts.length > 0 ? texts.join('<br>') : null;
}

/**
 * Render step text for one transformation usage, using its own render
 * template (or the hardcoded fallback) — ported from PreparationText.buildStepText.
 * Returns null if this usage has no transformation to render (e.g. a
 * description-only "rest the dough" style entry).
 */
function renderStepTextForUsage(
  ctx: RecipeTextContext,
  node: DisplayNode,
  children: DisplayNode[],
  trafoInfo: TransformationInfo,
  filterHidden: boolean,
): string | null {
  if (!trafoInfo.transformation) return null;

  const template = trafoInfo.transformation.renderTemplate;

  if (template) {
    return prependNewToolsPrefix(ctx, node, evaluateNodeTemplate(ctx, template, node, trafoInfo, children, filterHidden));
  }

  // Fallback: hardcoded logic
  const text = buildStepTextHardcoded(ctx, trafoInfo, children);
  return text ? prependNewToolsPrefix(ctx, node, capitalizeFirst(text)) : null;
}

/**
 * Prefix "Amb {tools}: " onto a step's text when the node generating it
 * introduces one or more new tool instances (see isNewToolInstance()) —
 * but only when the node isn't itself a bare tool node (isBareToolNode()):
 * a dedicated tool-only node's own title already says which tool it is,
 * so calling it out again here would be redundant. The rest of the text
 * gets decapitalized to read naturally after the colon, e.g.
 * "Amb l'olla: posa l'aigua a bullir."
 */
function prependNewToolsPrefix(ctx: RecipeTextContext, node: DisplayNode, text: string): string {
  if (!text || isBareToolNode(node)) return text;

  const newTools = (node.data.toolInfos ?? [])
    .filter(ti => !ti.hidden && isNewToolInstance(ctx, node, ti.tool.id, ti.fresh))
    .map(ti => ti.tool);
  if (newTools.length === 0) return text;

  const names = newTools.map(t => linkIngredient(t.name, node.id)).join(' i ');
  return `Amb ${names}: ${decapitalizeFirstVisible(text)}`;
}

/**
 * Whether a node performs at least one transformation — used for `kind`
 * classification and the bare-tool-node rule below, independent of which
 * specific usage (if any) is being rendered.
 */
function hasTransformation(data: DisplayNode['data']): boolean {
  return !!data.transformationInfos?.some(ti => ti.transformation);
}

/**
 * A node counts as a bare tool reference — excluded from the ingredient
 * join and shown in a trailing "en {tool}" phrase instead — only when it
 * has no identity of its own beyond the tool(s) it carries: no ingredient,
 * no transformation. A node that *also* performs a transformation (e.g.
 * "fry onions in a pot") keeps its normal child rendering, since the pot
 * there is incidental to real content (the fried onions) flowing through
 * it; only a dedicated tool-only step (a fresh tool leaf, or a pass-through
 * node created specifically to carry a tool forward with no ingredient of
 * its own) renders as the trailing phrase. A node whose tool(s) are all
 * hidden doesn't count either — nothing left to show.
 */
function isBareToolNode(node: DisplayNode): boolean {
  return !!node.data.toolInfos?.some(ti => !ti.hidden)
    && !node.data.ingredientInfo?.ingredient
    && !hasTransformation(node.data);
}

function resolveKind(data: DisplayNode['data']): TemplateNode['kind'] {
  if (isBareToolNode({ data } as DisplayNode)) return 'tool';
  if (data.ingredientInfo?.ingredient) return 'ingredient';
  if (hasTransformation(data)) return 'transformation';
  return undefined;
}

/**
 * Same Catalan-list join as formatChildren(), but with bare tool children
 * (see isBareToolNode()) pulled out into a trailing "en {tool}" phrase
 * instead of being joined into the main list.
 */
function formatChildrenWithTools(ctx: RecipeTextContext, children: DisplayNode[]): string {
  const toolChildren = children.filter(isBareToolNode);
  const restChildren = children.filter(c => !isBareToolNode(c));
  const mainText = formatChildren(ctx, restChildren);
  if (toolChildren.length === 0) return mainText;
  const toolNames = toolChildren.map(c => linkIngredient(c.name, c.id));
  return `${mainText} en ${toolNames.join(' i ')}`.trim();
}

/**
 * Evaluate a render template for one transformation usage and return
 * rendered HTML. `children` is the node's own children — the same set
 * every usage on this node renders over.
 */
function evaluateNodeTemplate(
  ctx: RecipeTextContext,
  template: string,
  node: DisplayNode,
  trafoInfo: TransformationInfo,
  children: DisplayNode[],
  filterHidden: boolean,
): string {
  const context = buildTemplateContext(ctx, node, trafoInfo, filterHidden);
  const result = evaluateTemplate(template, context);
  const text = result || buildStepTextHardcoded(ctx, trafoInfo, children);
  return capitalizeFirstVisible(text);
}

/**
 * Build the template context for one transformation usage on a node —
 * `params` comes from that specific usage, everything else (`self`) is
 * the node itself, shared across every usage.
 */
function buildTemplateContext(ctx: RecipeTextContext, node: DisplayNode, trafoInfo: TransformationInfo, filterHidden = true): TemplateContext {
  return {
    self: nodeToTemplateNode(ctx, node, undefined, filterHidden),
    params: trafoInfo.parameters ?? {},
    duration: trafoInfo.duration ?? null,
    graph: {
      node: (id: string) => {
        const n = ctx.store.getNode(id);
        return n ? nodeToTemplateNode(ctx, n, undefined, filterHidden) : null;
      },
    },
  };
}

/**
 * Convert a DisplayNode to a TemplateNode, with cycle detection.
 */
function nodeToTemplateNode(ctx: RecipeTextContext, node: DisplayNode, _visited?: Set<string>, filterHidden = true): TemplateNode {
  const visited = _visited ?? new Set<string>();

  if (visited.has(node.id)) {
    return {
      id: node.id, name: node.name, description: null, children: [], parents: [],
      quantityPrefix: undefined, kind: resolveKind(node.data),
    };
  }
  visited.add(node.id);

  const result: TemplateNode = {
    id: node.id,
    name: node.name,
    description: node.data.description ?? null,
    children: [],
    parents: [],
    quantityPrefix: getQuantityPrefix(ctx, node),
    kind: resolveKind(node.data),
  };

  result.children = (node.data.children || [])
    .map(cid => ctx.store.getNode(cid))
    .filter((cn): cn is DisplayNode => cn !== undefined && !isExcludedFromText(ctx, cn, filterHidden))
    .map(c => nodeToTemplateNode(ctx, c, visited, filterHidden));

  result.parents = getParentNodes(ctx, node)
    .filter(p => !isExcludedFromText(ctx, p, filterHidden))
    .map(p => nodeToTemplateNode(ctx, p, visited, filterHidden));

  return result;
}

/**
 * Get parent DisplayNodes for a node from the node pool.
 */
function getParentNodes(ctx: RecipeTextContext, node: DisplayNode): DisplayNode[] {
  return ctx.store.getParentIds(node.id)
    .map(id => ctx.store.getNode(id))
    .filter((n): n is DisplayNode => !!n);
}

/**
 * Hardcoded fallback for building step text for one transformation usage.
 * Duration (if set) is shown separately, as a right-aligned clock badge
 * next to the row — see buildPreparationHtml()'s own use of
 * durationBadgeHtml() — not appended into this text.
 */
function buildStepTextHardcoded(ctx: RecipeTextContext, trafoInfo: TransformationInfo, children: DisplayNode[]): string {
  if (children.length === 0) return '';

  const trafoLabel = getTrafoLabel(trafoInfo);
  return `${trafoLabel} ${formatChildrenWithTools(ctx, children)}`.trim();
}

/**
 * Get the transformation label for one usage.
 */
function getTrafoLabel(trafoInfo: TransformationInfo): string {
  if (!trafoInfo.transformation?.name) return '';
  return capitalizeFirst(trafoInfo.transformation.name);
}

/**
 * Format children as a natural Catalan list.
 */
function formatChildren(ctx: RecipeTextContext, children: DisplayNode[]): string {
  if (children.length === 0) return '';

  const parts = children.map(c => formatChild(ctx, c));

  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} i ${parts[1]}`;

  const last = parts[parts.length - 1];
  const rest = parts.slice(0, -1).join(', ');
  return `${rest} i ${last}`;
}

/**
 * Get the quantity prefix for a node, e.g. "300 ml d'" or "3 u. de ".
 */
function getQuantityPrefix(ctx: RecipeTextContext, node: DisplayNode): string | undefined {
  const qty = node.data.ingredientInfo?.quantity;
  const ingredientName = node.data.ingredientInfo?.ingredient?.name;
  if (qty && ingredientName) {
    return `${formatQuantityDisplay(ctx, qty)} ${preposition(ingredientName)}`;
  }
  return undefined;
}

/**
 * Format a single child node as HTML.
 */
function formatChild(ctx: RecipeTextContext, child: DisplayNode): string {
  const qtyPrefix = getQuantityPrefix(ctx, child);
  const ingredientName = child.data.ingredientInfo?.ingredient?.name;
  const displayName = child.name;

  if (qtyPrefix && ingredientName) {
    return `${qtyPrefix}${linkIngredient(ingredientName, child.id)}`;
  }

  if (ingredientName) {
    return linkIngredient(ingredientName, child.id);
  }

  if (displayName && displayName !== 'Unnamed Node') {
    return linkIngredient(displayName, child.id);
  }

  return linkIngredient(child.id, child.id);
}

/**
 * Format a quantity with a unit display name.
 */
function formatQuantityDisplay(ctx: RecipeTextContext, qty: Quantity, applyScaling = true): string {
  const scaled = applyScaling ? ctx.scaleAmount(qty.amount) : qty.amount;
  const unitStr = unitCache.getDisplayName(qty.unit, scaled);
  const amountStr = formatNumber(scaled);
  return `${amountStr} ${unitStr}`;
}

/**
 * Create a clickable/hoverable ingredient span.
 */
function linkIngredient(name: string, nodeId: string): string {
  return `<span class="preparation-ingredient" data-node-id="${nodeId}">${escapeHtml(name)}</span>`;
}

/**
 * Render ingredients HTML for the children of a node.
 * Groups by ingredient ID, merges quantities by unit, produces clickable spans.
 */
export function renderIngredientsTextForNode(ctx: RecipeTextContext, node: DisplayNode): string | null {
  // Get all descendant leaf nodes (no children of their own) with ingredients.
  // This is used for folded nodes to show what's inside the subgraph.
  const descendants = ctx.store.getDescendantsInPool(node);
  const ingredientNodes = descendants.filter(n => {
    const hasChildren = (n.data.children?.length ?? 0) > 0;
    return !hasChildren && !n.data.hidden && n.data.ingredientInfo?.ingredient;
  });
  if (ingredientNodes.length === 0) return null;

  // Group by ingredient ID
  const groups = new Map<string, DisplayNode[]>();
  for (const n of ingredientNodes) {
    const key = n.data.ingredientInfo!.ingredient!.id;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(n);
  }

  const seenKeys = new Set<string>();
  let html = '';

  for (const n of ingredientNodes) {
    const key = n.data.ingredientInfo!.ingredient!.id;
    if (seenKeys.has(key)) continue;
    seenKeys.add(key);

    const groupNodes = groups.get(key)!;
    const ingredient = groupNodes[0].data.ingredientInfo!.ingredient!;

    // Merge quantities by unit
    const quantities = groupNodes
      .map(g => g.data.ingredientInfo?.quantity)
      .filter((q): q is Quantity => q != null);
    const merged = mergeQuantities(quantities);

    // Build display string
    const qtyStr = merged.length > 0
      ? merged.map(q => {
          const scaledAmount = ctx.scaleAmount(q.amount);
          const unitStr = unitCache.getDisplayName(q.unit, scaledAmount);
          return `${formatNumber(scaledAmount)} ${unitStr}`;
        }).join(' + ')
      : '';

    html += '<div class="flex items-start gap-2 py-1">';
    html += '  <span class="text-[var(--accent)] flex-shrink-0">&gt;</span>';
    html += '  <span>';  // wrapper to keep qty+ingredient as a single flex child
    if (qtyStr) {
      const prep = preposition(ingredient.name);
      html += `<span class="text-[#e0e0e0] font-mono text-sm">${qtyStr} ${prep}</span>`;
      html += `<span class="text-[#e0e0e0] font-mono text-sm">${escapeHtml(ingredient.name)}</span>`;
    } else {
      html += `  <span class="text-[#e0e0e0] font-mono text-sm">${escapeHtml(ingredient.name)}</span>`;
    }
    html += '  </span>';
    html += '</div>';

    // Comment lines: node description, ingredient notes, transformation notes
    const groupNode = groupNodes[0];
    const commentLines: string[] = [];
    if (groupNode.data.description) commentLines.push(groupNode.data.description);
    if (groupNode.data.ingredientInfo?.description) commentLines.push(groupNode.data.ingredientInfo.description);
    for (const ti of groupNode.data.transformationInfos ?? []) {
      if (ti.description) commentLines.push(ti.description);
    }
    for (const line of commentLines) {
      html += `<div class="text-xs text-[#888] font-mono ml-6 mb-1">// ${escapeHtml(line)}</div>`;
    }
  }

  return html || null;
}
